import React from "react"
import {Resource} from "../../resources/common"
import moment from "moment"
import numeral from "numeral"
import {TagsViewerKeyValue} from "../Tags/TagsViewer"
import {useNavigate} from "react-router-dom"
import {OrgName} from "../OrgName/OrgName"
import ReactTooltip from "react-tooltip"
import BodyPortal from "../BodyPortal/BodyPortal"
import {KeyValueCard, KeyValueCardContainer, KeyValueCardKey, KeyValueCardValue} from "../KeyValueCard/KeyValueCard"
import {getCommonCustomerDetails} from "../Customers/CustomerCardView"
import {getCustomerHref, getCustomerName, resolveCustomers} from "./checkDepositUtils"

export const checkDepositTooltipValue =
	"The funds from checks that are deposited before the daily cutoff, are received by Unit on the next business day, and held for the clearing period defined on the account’s deposit product"

interface CheckDepositDetailsProps {
	amount: number
	number?: string
	customerId?: number
	customerIds?: Array<number | string>
	accountId?: number
	depositDate: Date
	description: string
	include?: Resource[]
	settlementDate?: Date
	orgId?: string
	tags?: {[key: string]: string}
	title?: string
	singleColumn?: boolean
	className?: string
	withCustomerDetails?: boolean
	namesAtUnit?: React.ReactNode
}

export function CheckDepositDetails({
	amount,
	number,
	customerId,
	customerIds,
	orgId,
	accountId,
	depositDate,
	description,
	include,
	settlementDate,
	tags,
	title,
	className,
	singleColumn = false,
	withCustomerDetails,
	namesAtUnit,
}: CheckDepositDetailsProps) {
	const navigate = useNavigate()
	const customers = resolveCustomers(customerId, customerIds, include)
	const primaryCustomer = customers[0]

	const {doingBusinessAs, authorizedUsers} =
		(withCustomerDetails &&
			(primaryCustomer?.type == "individualCustomer" || primaryCustomer?.type == "businessCustomer") &&
			getCommonCustomerDetails(primaryCustomer)) ||
		{}

	const account =
		accountId && include
			? include.find((resource) => resource.type == "depositAccount" && resource.id == accountId.toString())
			: undefined
	const accountName = account?.type == "depositAccount" ? account.attributes.name : undefined

	return (
		<KeyValueCard className={className} title={title} singleColumn={singleColumn}>
			<KeyValueCardContainer>
				{amount && (
					<>
						<KeyValueCardKey> Check Amount</KeyValueCardKey>
						<KeyValueCardValue> {numeral(amount / 100).format("$0,0.00")} </KeyValueCardValue>
					</>
				)}

				{number && (
					<>
						<KeyValueCardKey> Check Number</KeyValueCardKey>
						<KeyValueCardValue> {number} </KeyValueCardValue>
					</>
				)}

				{orgId && (
					<>
						<KeyValueCardKey> Org</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<OrgName orgId={orgId.toString()} element={"span"} />
						</KeyValueCardValue>
					</>
				)}

				{customers.length > 0 && (
					<>
						<KeyValueCardKey> Customer</KeyValueCardKey>
						<KeyValueCardValue>
							{customers.map((customer, index) => {
								const customerName = getCustomerName(customer)
								if (!customerName) {
									return null
								}
								const href = getCustomerHref(customer)
								return (
									<React.Fragment key={customer.id}>
										{index > 0 && ", "}
										<a
											className="link"
											href={href}
											onClick={(e) => {
												e.preventDefault()
												e.stopPropagation()
												navigate(href)
											}}
										>
											{customerName}
										</a>
									</React.Fragment>
								)
							})}
						</KeyValueCardValue>
					</>
				)}

				{accountId && (
					<>
						<KeyValueCardKey> Account ID</KeyValueCardKey>
						<KeyValueCardValue>
							<a
								className="link"
								onClick={(e) => {
									e.preventDefault()
									e.stopPropagation()
									if (accountId) {
										navigate(`/accounts/${accountId}`)
									}
								}}
							>
								{accountId}
							</a>
						</KeyValueCardValue>
					</>
				)}

				{accountId && accountName && account && (
					<>
						<KeyValueCardKey>Account Name</KeyValueCardKey>
						<KeyValueCardValue>
							<a
								className="link"
								onClick={(e) => {
									e.preventDefault()
									e.stopPropagation()
									if (accountId) {
										navigate(`/accounts/${accountId}`)
									}
								}}
							>
								{accountName}
							</a>
						</KeyValueCardValue>
					</>
				)}

				{depositDate && (
					<>
						<KeyValueCardKey> Deposit Date</KeyValueCardKey>
						<KeyValueCardValue> {moment(depositDate).format("MM/DD/YYYY h:mm A")} </KeyValueCardValue>
					</>
				)}

				{settlementDate && (
					<>
						<KeyValueCardKey> Expected Completion Date</KeyValueCardKey>
						<KeyValueCardValue>
							<span
								data-tip="React-tooltip"
								data-for={`check-deposit-drawer-expected-completion-time-tooltip-${number}`}
							>
								{moment(settlementDate).format("MM/DD/YYYY")}
							</span>
							<BodyPortal>
								<ReactTooltip
									className="tooltip-info"
									id={`check-deposit-drawer-expected-completion-time-tooltip-${number}`}
									disable={!settlementDate}
									effect="solid"
								>
									{checkDepositTooltipValue}
								</ReactTooltip>
							</BodyPortal>
						</KeyValueCardValue>
					</>
				)}

				{tags && (
					<>
						<KeyValueCardKey> Tags</KeyValueCardKey>
						<KeyValueCardValue>
							<TagsViewerKeyValue tags={tags} />
						</KeyValueCardValue>
					</>
				)}

				{description && (
					<>
						<KeyValueCardKey> Description</KeyValueCardKey>
						<KeyValueCardValue> {description} </KeyValueCardValue>
					</>
				)}
				{doingBusinessAs && (
					<>
						<KeyValueCardKey>Doing Business As</KeyValueCardKey>
						<KeyValueCardValue>{doingBusinessAs}</KeyValueCardValue>
					</>
				)}
				{authorizedUsers && (
					<>
						<KeyValueCardKey>Authorized Users</KeyValueCardKey>
						<KeyValueCardValue>{authorizedUsers}</KeyValueCardValue>
					</>
				)}
				{namesAtUnit && (
					<>
						<KeyValueCardKey>Names At Unit</KeyValueCardKey>
						<KeyValueCardValue>{namesAtUnit}</KeyValueCardValue>
					</>
				)}
			</KeyValueCardContainer>
		</KeyValueCard>
	)
}
